import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { AppService } from './app.service';
import { UtilsService } from './utils.service';
import {ModalController, NavController} from '@ionic/angular';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {SentryService} from "./sentry.service";

@Injectable({
  providedIn: 'root'
})
export class InterceptorService implements HttpInterceptor {

  constructor(
    private app: AppService,
    private storage: StorageService,
    private utils: UtilsService,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private sentry: SentryService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Add the authorization header if none is present.
    if (!request.headers.has('Authorization')) {
      const authorizationToken: string = this.storage.get('AuthToken');
      request = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${authorizationToken}`)
      });
    }

    // Identify kiosk requests to the API. The backend uses this to apply
    // kiosk-appropriate access control (gated by the core user-profile
    // permission rather than web-portal feature permissions), mirroring how
    // the mobile app sends 'Platform: Mobile'. Note: the value is 'Kiosk' (not
    // 'Mobile') so the API keeps the kiosk on its default account context.
    if (!request.headers.has('Platform')) {
      request = request.clone({
        headers: request.headers.set('Platform', 'Kiosk')
      });
    }

    // Uploading files does not need content type application/json.
    if (!(request.body instanceof FormData)) {
      if (!request.headers.has('Content-Type')) {
        request = request.clone({
          headers: request.headers.set('Content-Type', 'application/json')
        });
      }
      if (!request.headers.has('Accept')) {
        request = request.clone({
          headers: request.headers.set('Accept', 'application/json')
        });
      }
    }

    // Requests where a 401 means "the credentials posted in this request were wrong", NOT "the
    // kiosk's own auth token is invalid". A visitor signing in at the kiosk posts their own
    // credentials to v2/login while the kiosk itself stays authenticated, so a failure there must
    // never log the kiosk out. The calling page surfaces the error to the visitor instead.
    // Matched on the exact path so v1/tablet/signin is not caught by this.
    const isCredentialCheckRequest: boolean = request.url.split('?')[0].endsWith('/v2/login');

    // Define the loader as a null object.
    let loader: Promise<HTMLIonLoadingElement> = null;

    // Create a new loader if the request does not have the X-Skip-Loader header.
    if ( !request.headers.has('X-Skip-Loader') ) {
      loader = this.utils.showLoader();
    }

    return next.handle(request).pipe(tap(
      (response: any) => {

        // Adopt a refreshed auth token when the API rotates it (only present when the previous
        // token was stale, e.g. after an RBAC change). Storing it keeps the kiosk on a valid,
        // current-permission token and stops the API from re-issuing tokens on every request.
        if ( response instanceof HttpResponse ) {
          const refreshedToken: string = response.headers.get('X-Refreshed-Token');
          if ( refreshedToken ) {
            this.storage.set('AuthToken', refreshedToken);
          }
        }

        // Dismiss the loader if the request does not have the X-Skip-Loader header.
        if ( !request.headers.has('X-Skip-Loader') ) {
          loader.then(_loader => {
            // Dismiss the loader.
            _loader.dismiss();
          });
        }
      },
      error => {
        // Sign the kiosk out when the API rejects the token. Angular surfaces a 401 as an error
        // response, so this MUST be handled here and not in the success callback above.
        if(error.status === 401 && this.app.isAuthenticated() && !isCredentialCheckRequest) {
          // Close any open modals so the user is not left on a stale screen.
          if(this.modalCtrl) {
            this.modalCtrl.dismiss();
          }
          // Clear the storage. This preserves the selected region.
          this.storage.clear();
          // Remove Authentication
          this.app.isAuthenticated(false);
          // Remove System Admin status
          this.app.isSystemAdmin(false);
          // Clear Sentry User and Tags
          this.sentry.setUser(null);
          this.sentry.setEmployer(null);
          this.sentry.setSite(null);
          // Show authentication failure message
          this.utils.showToast('Authentication failure. You are now being logged out.');
          // Redirect the user
          this.navCtrl.navigateRoot(['/']);
        } else if(error.status >= 500) {
          if(error.status == 503 && this.app.isAuthenticated()) {
            // Server is in Maintenance Mode
            if(this.modalCtrl) {
              this.modalCtrl.dismiss(); // Close Modals
            }
            // Clear the storage
            this.storage.clear();
            // Remove Authentication
            this.app.isAuthenticated(false);
            // Remove System Admin status
            this.app.isSystemAdmin(false);
            // Clear Sentry User and Tags
            this.sentry.setUser(null);
            this.sentry.setEmployer(null);
            this.sentry.setSite(null);
            // Show error message
            this.utils.showToast('You are now being logged out. Something went wrong with our server, try again later.');
            // Redirect the user
            this.navCtrl.navigateRoot(['/']);
          } else {
            this.utils.showToast(this.utils.getServerErrorMessage());
          }
        } else if(error.status === 0) {
          // The request never reached the API. Either this tablet has no connectivity, or the server
          // is not answering (commonly while a deployment restarts it). Without this branch a status
          // 0 was silent: the loader below dismissed and the screen simply did not advance, which on
          // an unattended kiosk leaves a visitor stuck mid sign-in with nothing to act on.
          this.utils.showConnectivityErrorToast();
        }

        // Dismiss the loader if the request does not have the X-Skip-Loader header.
        if ( !request.headers.has('X-Skip-Loader') ) {
          loader.then(_loader => {
            // Dismiss the loader.
            _loader.dismiss();
          });
        }
      }
    ));
  }
}
